import PropTypes from 'prop-types';
import './style.scss';

import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { Badge } from 'primereact/badge';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Menu } from 'primereact/menu';
import { Tag } from 'primereact/tag';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { Tooltip } from 'primereact/tooltip';
import { getAll as getAllPrograms } from 'apis/programs.api';
import { searchForCouncil as getAllProjectProposals } from 'apis/projectProposals.api';
import { getAllResearchFieldsByUserId } from 'apis/researchFields.api';

import { Confirm } from 'features/ReviseProjectProposal/components';

import { AbilityContext } from 'components/Can';
import Pagination from 'components/Pagination';
import {
  COUNCIL_STATUS,
  NON_PRINTABLE_REGEX,
  institutionalSpecialistSetting,
  specialistSetting,
} from 'constant';
import RequestUpdateForm from 'features/ReviseProjectProposal/components/RequestUpdateForm';
// import { defaultProjecProposalsFilters } from 'features/ReviseProjectProposal/defaultValues';
import { mapError, getRoles, getProjectTypes, filterProjectTypes } from 'utils/func';
import useSelectionProposal from 'utils/hooks/useSelectionProposal';
import { totalExpenditureSchema } from 'features/ReviseProjectProposal/validation';

const councilTypeToReviseLogTypeMap = {
  preliminarySelectionCouncil: 'REQUEST_EXPLANATION_PREQUALIFICATION',
  specializationCouncil: 'REQUEST_EXPLANATION_SPECIALIZATION',
  financeCouncil: 'REQUEST_EXPLANATION_FINANCE',
};

const councilStatusSeverityMap = {
  notReviewed: 'warning',
  notQualified: 'danger',
  qualified: 'success',
};

function ViewListProjectProposal({
  onSelectedChange,
  projectProposals,
  setVisible,
  curMembers,
  councilTypeSelected,
}) {
  const roles = getRoles();
  // #region data
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams({ replace: true });
  const { councilId } = useParams();
  const dialogConfirmRef = useRef();
  const dialogRequestUpdateRef = useRef();
  const approvalMenu = useRef(null);
  const [pagination, setPagination] = useState({
    first: parseInt(searchParams.get('first'), 10) || 0,
    rowsPerPage: 10,
  });
  const [searchProposalName, setSearchProposalName] = useState('');
  const [showMode, setShowMode] = useState('hideDeletedProjects');
  const ability = useContext(AbilityContext);
  const [filtersTable, setFiltersTable] = useState({
    _vietnameseName: { value: null },
    _projectType: { value: null },
    _ownerName: { value: null },
    _organization: { value: null },
    _researchField: { value: null },
    _researchType: { value: null },
    _totalExpenditure: { value: null },
    _program: { value: null },
    _submittedAt: { value: null },
    _councilType: { value: null },
    _status: { value: null },
  });

  // background filters, sync with localStorage
  const [filters, setFilters] = useState({
    vietnameseName: null,
    projectType: null,
    ownerName: null,
    organizationId: null,
    researchFieldId: null,
    researchTypeId: null,
    totalExpenditure: { min: null, max: null },
    programId: null,
    submittedAt: { start: null, end: null },
    councilTypeId: null,
    statusName: null,
  });

  // applied filters, change every time click on Apply and Clear button for update
  const [appliedFilters, setAppliedFilters] = useState({
    vietnameseName: null,
    projectType: null,
    ownerName: null,
    organizationId: null,
    researchFieldId: null,
    researchTypeId: null,
    totalExpenditure: { min: null, max: null },
    programId: null,
    submittedAt: { start: null, end: null },
    councilTypeId: councilTypeSelected.id,
    statusName: null,
  });

  const [filterByProgram, setFilterByProgram] = useState([false, null]);
  const [oldFilterPrograms, setOldFilterPrograms] = useState([]);

  const projectTypes = filterProjectTypes(getProjectTypes(t), roles);

  const {
    data: _data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery(
    [
      'projectProposals',
      pagination,
      appliedFilters,
      councilId,
      specialistSetting,
      institutionalSpecialistSetting,
    ],
    ({
      queryKey: [
        ,
        _pagination,
        _filters,
        _councilId,
        _specialistSetting,
        _institutionalSpecialistSetting,
      ],
    }) =>
      getAllProjectProposals(
        _pagination,
        _filters,
        _councilId,
        _specialistSetting,
        _institutionalSpecialistSetting
      ),
    {
      staleTime: 0,
      enabled: !!appliedFilters.councilTypeId,
    }
  );

  const data = useMemo(() => _data?.data ?? [], [_data]);

  const { data: researchFieldsData } = useQuery(
    ['projectProposalResearchFieldsByUserId'],
    getAllResearchFieldsByUserId
  );
  const researchFields = useMemo(
    () =>
      researchFieldsData?.data.map((e) => ({
        id: e.id,
        name: e?.code ? t(`researchFields.${e.code}`) : e.name,
      })) ?? [],
    [researchFieldsData, t]
  );

  const { data: programData } = useQuery(['program'], getAllPrograms);
  const programs = useMemo(() => programData?.data ?? [], [programData]);

  const {
    selectedItems,
    isSelectedAll,
    handleAllRowSelect,
    handleAllRowUnselect,
    handleRowSelect,
    handleRowUnselect,
    setSelectedItems,
    setIsSelectedAll,
  } = useSelectionProposal(
    data,
    pagination,
    curMembers,
    councilId,
    councilTypeSelected,
    setFilterByProgram
  );

  const approvalItems = [
    {
      label: t('projectProposal.viewList.approve'),
      icon: 'pi pi-check',
      command: () => {},
    },
    { separator: true },
    {
      label: t('projectProposal.viewList.changeRequest'),
      icon: 'pi pi-refresh',
      command: () => {},
    },
  ];

  // #endregion Data

  // #region Event
  useEffect(() => {
    setShowMode(showMode);
  }, [t]);

  useEffect(() => {
    const localFilters = JSON.parse(
      localStorage.getItem(`admin-${location.pathname.slice(1)}-filters`) ?? '{}'
    );

    const modifySubmittedAt = {};
    if (localFilters?.submittedAt) {
      const { start, end } = localFilters.submittedAt;
      if (start || end) {
        modifySubmittedAt.start = start ? new Date(start) : '';
        modifySubmittedAt.end = end ? new Date(end) : '';
      }
    }

    setFilters({
      ...localFilters,
      submittedAt: modifySubmittedAt,
      totalExpenditure: { min: undefined, max: undefined },
      // statusName: [PROJECT_PROPOSAL_STATUSES.SUBMIT, PROJECT_PROPOSAL_STATUSES.APPROVED],
      councilTypeId: councilTypeSelected.id,
    });
    setAppliedFilters({
      ...localFilters,
      submittedAt: modifySubmittedAt,
      totalExpenditure: { min: undefined, max: undefined },
      // statusName: [PROJECT_PROPOSAL_STATUSES.SUBMIT, PROJECT_PROPOSAL_STATUSES.APPROVED],
      councilTypeId: councilTypeSelected.id,
    });

    // Set rows per page in localStorage
    const localRowsPerPage = parseInt(
      localStorage.getItem(`admin-${location.pathname.slice(1)}-rowsPerPage`),
      10
    );
    if (localRowsPerPage) {
      setPagination((cur) => ({ ...cur, rowsPerPage: localRowsPerPage }));
    }

    // Set current page (first) with searchParams
    const currentFirst = parseInt(searchParams.get('first'), 10) || 0;
    setPagination((cur) => ({ ...cur, first: currentFirst }));
  }, []);

  useEffect(() => {
    if (projectProposals.length > 0) {
      setSelectedItems(projectProposals);
    }
  }, [projectProposals]);

  useEffect(() => {
    if (filterByProgram[0] && !!filterByProgram[1]) {
      const { programId } = appliedFilters;
      setOldFilterPrograms(programId);
      const newFilters = { ...filters, programId: [filterByProgram[1]] };
      setFilters(newFilters);
      setAppliedFilters(newFilters);
    } else if (filterByProgram[0] && filterByProgram[1] === null) {
      const newFilters = { ...filters, programId: oldFilterPrograms };
      setFilters(newFilters);
      setAppliedFilters(newFilters);
    }
  }, [filterByProgram]);

  // #endregion Event

  // #region Render
  const isRowSelectable = (event) => event?.data.deletedAt === null;

  const rowClassName = (value) => (value?.deletedAt !== null ? 'p-disabled' : '');

  const formatName = (value) => {
    if (value?.deletedAt === null) {
      return (
        <Link
          to={`/projects-revise/${value?.reference}?tab=0`}
          target="_blank"
          rel="noopener noreferrer"
          className="hover:underline"
        >
          {value?.vietnameseName}
        </Link>
      );
    }

    return <span>{value?.vietnameseName}</span>;
  };
  const vietnameseNameFilter = () => (
    <InputText
      value={filters.vietnameseName}
      onChange={(e) => {
        const newFilters = { ...filters, vietnameseName: e.target.value };
        setFilters(newFilters);
      }}
      placeholder={t('projectProposal.viewList.name')}
      className="p-column-filter"
    />
  );

  const projectTypeFilter = () => (
    <MultiSelect
      value={filters.projectType}
      onChange={(e) => {
        const newFilters = { ...filters, projectType: e.value };
        setFilters(newFilters);
      }}
      options={projectTypes}
      optionLabel="name"
      optionValue="value"
      filter
      showClear
      placeholder={t('projectProposal.viewList.projectType')}
      maxSelectedLabels={3}
      style={{ maxWidth: '16rem', width: '12.5rem' }}
    />
  );
  const formatProjectType = (value) => t(`projectTypes.${value.projectType}`);

  const formatOwner = (value) => {
    const owner = value?.projectProposalContacts.find(
      (contact) => contact?.projectRoleId === 'e609455d-e481-492e-8fca-f631ebf0d464'
    );
    if (owner) return <div>{owner.fullname}</div>;
    return null;
  };
  const ownerNameFilter = () => (
    <InputText
      value={filters.ownerName}
      onChange={(e) => {
        const newFilters = { ...filters, ownerName: e.target.value };
        setFilters(newFilters);
      }}
      placeholder={t('projectProposal.viewList.owner')}
      className="p-column-filter"
    />
  );

  const formatResearchFields = (value) => (
    <div>
      {value?.projectProposalResearchFields
        ?.sort((a, b) => {
          if (a.priority === null) return 1;
          if (b.priority === null) return -1;
          if (a.priority === b) return 0;
          return a.priority < b.priority ? -1 : 1;
        })
        ?.map((researchField, index) => (
          <p key={researchField.researchField.id} className="text-left m-0">
            {`(${researchField?.priority ?? index + 1}) ${t(
              `researchFields.${researchField.researchField.code}`
            )}`}
          </p>
        ))}
    </div>
  );
  const researchFieldFilter = () => (
    <MultiSelect
      value={filters.researchFieldId}
      onChange={(e) => {
        const newFilters = { ...filters, researchFieldId: e.value };
        setFilters(newFilters);
      }}
      options={researchFields}
      optionLabel="name"
      optionValue="id"
      filter
      showClear
      placeholder={t('projectProposal.viewList.researchField')}
      maxSelectedLabels={3}
      style={{ maxWidth: '22rem', width: '18.5rem' }}
    />
  );

  const programFilter = () => (
    <MultiSelect
      value={filters.programId}
      onChange={(e) => {
        const newFilters = { ...filters, programId: e.value };
        setFilters(newFilters);
      }}
      options={programs}
      optionLabel="name"
      optionValue="id"
      filter
      showClear
      placeholder={t('projectProposal.viewList.program')}
      style={{ maxWidth: '22rem', width: '18.5rem' }}
    />
  );

  const formatCouncil = (value) => {
    const projectProposalCouncils = value?.projectProposalCouncils ?? []; // sorted ASC by councilType.order

    if (projectProposalCouncils?.length > 0) {
      return projectProposalCouncils?.map((ppCouncil, index) => {
        const correspondingReviseLog = (value?.projectProposalReviseLogs ?? [])?.find(
          (item) => item?.type === councilTypeToReviseLogTypeMap?.[ppCouncil?.councilType?.code]
        );
        const hasRequest = !!ppCouncil?.councilStatus && !!correspondingReviseLog;

        // 7. có yc giải trình, chủ nhiệm đã giải trình và CHỐT
        // TODO

        let requestStatusIcon = {};
        // có yc giải trình
        if (hasRequest) {
          // 4. có yc giải trình, chủ nhiệm chưa giải trình
          if (correspondingReviseLog?.isOpened && !correspondingReviseLog?.ownerComment) {
            requestStatusIcon = {
              tooltip: t('projectProposal.requestExplanation.statuses.ownerNotAddExplanationYet'),
              className: 'text-red-600',
              icon: 'pi pi-times-circle',
            };
          }
          // 5. có yc giải trình, chủ nhiệm đã giải trình
          if (!correspondingReviseLog?.isOpened && correspondingReviseLog?.ownerComment) {
            requestStatusIcon = {
              tooltip: t('projectProposal.requestExplanation.statuses.ownerHasAddedExplanation'),
              className: 'text-green-600',
              icon: 'pi pi-check-circle',
            };
          }
          // 6. có yc giải trình, chủ nhiệm đã giải trình, cv yêu cầu gtrinh lại
          if (correspondingReviseLog?.isOpened && correspondingReviseLog?.ownerComment) {
            requestStatusIcon = {
              tooltip: t(
                'projectProposal.requestExplanation.statuses.ownerNotUpdateExplanationYet'
              ),
              className: 'text-orange-500',
              icon: 'pi pi-replay',
            };
          }
        }
        return (
          <div key={ppCouncil?.id ?? index} className="flex flex-column align-items-start">
            <span className="my-1">- {ppCouncil?.councilType?.name}</span>
            <div className="flex flex-wrap align-items-center">
              <Tag
                style={{
                  maxWidth: '8rem',
                  minWidth: '4rem',
                  textAlign: 'center',
                }}
                severity={
                  councilStatusSeverityMap[ppCouncil?.councilStatus ?? COUNCIL_STATUS.NOT_REVIEWED]
                }
                value={t(
                  `council.viewList.${ppCouncil?.councilStatus ?? COUNCIL_STATUS.NOT_REVIEWED}`
                )}
                rounded
              />

              {hasRequest && (
                <>
                  <i
                    className={`request-${ppCouncil?.id} pi pi-file p-overlay-badge ml-1 text-xl`}
                    data-pr-tooltip={
                      requestStatusIcon?.tooltip ??
                      t(
                        'projectProposal.requestExplanation.statuses.specialistNotRequestExplanationYet'
                      )
                    }
                  >
                    <Badge
                      value={<i className={`${requestStatusIcon?.icon} font-bold`} />}
                      className={requestStatusIcon?.className}
                      style={{ backgroundColor: 'transparent' }}
                    />
                  </i>
                  <Tooltip target={`.request-${ppCouncil?.id}`} />
                </>
              )}
            </div>
          </div>
        );
      });
    }
    return null;
  };

  const formatStatus = (value) => {
    const statusColorMapping = {
      revise: 'warning',
      new: 'info',
      submit: 'success',
      approved: 'success',
    };

    return (
      <Tag
        style={{ maxWidth: '8rem' }}
        severity={statusColorMapping[value?.status.name]}
        value={t(`statuses.${value?.status.name}`)}
        rounded
      />
    );
  };

  const filterClearTemplate = (options) => (
    <Button
      type="button"
      label={t('employee.viewList.clear')}
      onClick={() => {
        setIsSelectedAll(false);
        options.filterClearCallback();
      }}
      severity="secondary"
      outlined
    />
  );

  const filterApplyTemplate = (options) => (
    <Button
      type="button"
      label={t('employee.viewList.apply')}
      onClick={() => {
        options.filterApplyCallback();
        localStorage.setItem(
          `admin-${location.pathname.slice(1)}-filters`,
          JSON.stringify(filters)
        );
      }}
      disabled={
        !totalExpenditureSchema.isValidSync({
          min: filters?.totalExpenditure?.min ?? undefined,
          max: filters?.totalExpenditure?.max ?? undefined,
        })
      }
      severity="success"
    />
  );

  const filterActiveStyle = (field) =>
    filters[field] && (filters[field]?.length > 0 || Object.values(filters[field]).some((e) => e))
      ? { color: '#22C55E' }
      : { color: '#6c757d' };

  const renderHeader = () => (
    <div className="relative p-fluid flex align-items-end">
      <div className="w-10">
        <label className="mb-2">
          {t('council.createForManyProjectProposal.projectProposalDialog.labelSearchProposalName')}
        </label>
        <span className="p-input-icon-right">
          <i
            className="pi pi-times hover:surface-200 p-1 border-circle cursor-pointer"
            onClick={() => {
              setSearchProposalName('');
              setIsSelectedAll(false);
              const newFilters = { ...filters, vietnameseName: '' };
              setFilters(newFilters);
              setAppliedFilters(newFilters);
              localStorage.setItem(
                `admin-${location.pathname.slice(1)}-filters`,
                JSON.stringify(newFilters)
              );
            }}
          />
          <InputText
            className="mt-2"
            placeholder={t(
              'council.createForManyProjectProposal.projectProposalDialog.placeholderSearchProposalName'
            )}
            onChange={(e) => {
              const formattedValue = e.target.value?.replace(NON_PRINTABLE_REGEX, '');
              setSearchProposalName(formattedValue);
            }}
            value={searchProposalName}
          />
        </span>
      </div>
      <div className="ml-4">
        <Button
          label="Tìm kiếm"
          severity="info"
          type="button"
          onClick={() => {
            const newFilters = { ...filters, vietnameseName: searchProposalName };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
          }}
          className="mr-2 w-8rem"
        />
      </div>
    </div>
  );

  if (isError) {
    return <h4 className="text-red-500">{mapError(t, error)}</h4>;
  }

  return (
    <div className="relative p-formgroup-inline">
      {/* Custom checkbox selectAll */}
      {!isLoading && data?.data?.length > 0 && appliedFilters?.programId?.length === 1 && (
        <Checkbox
          style={{ position: 'absolute', top: '108px', left: '14px', zIndex: 10 }}
          checked={isSelectedAll}
          onChange={() => (!isSelectedAll ? handleAllRowSelect() : handleAllRowUnselect())}
        />
      )}
      <DataTable
        value={data?.data}
        className="project-list"
        dataKey="reference"
        header={renderHeader}
        showGridlines
        scrollable
        selectionMode="multiple"
        selection={selectedItems}
        showSelectAll={false}
        onRowSelect={handleRowSelect}
        onRowUnselect={handleRowUnselect}
        isDataSelectable={isRowSelectable}
        rowClassName={rowClassName}
        filters={filtersTable}
        onFilter={(e) => setFiltersTable(e.filters)}
        filterDisplay="menu"
        loading={isLoading}
        emptyMessage={t('projectProposal.viewList.emptyMessage')}
      >
        {ability.can('create', 'council') ? (
          <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} frozen />
        ) : null}
        <Column
          header={t('projectProposal.viewList.name')}
          headerStyle={filterActiveStyle('vietnameseName')}
          body={formatName}
          filter
          filterField="_vietnameseName"
          filterElement={vietnameseNameFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMenu
          showFilterMatchModes={false}
          onFilterClear={() => {
            const newFilters = { ...filters, vietnameseName: '' };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ minWidth: '10rem', maxWidth: '16rem' }}
          frozen
          alignFrozen="left"
        />
        <Column
          field="projectType"
          headerStyle={{ ...filterActiveStyle('projectType'), justifyContent: 'center' }}
          header={t('projectProposal.viewList.projectType')}
          body={formatProjectType}
          filter
          filterField="_projectType"
          filterElement={projectTypeFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMenu
          showFilterMatchModes={false}
          onFilterClear={() => {
            const newFilters = { ...filters, projectType: [] };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ minWidth: '8rem', textAlign: 'center' }}
        />
        <Column
          header={t('projectProposal.viewList.owner')}
          headerStyle={{ ...filterActiveStyle('ownerName'), justifyContent: 'center' }}
          body={formatOwner}
          filter
          filterField="_ownerName"
          filterElement={ownerNameFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMenu
          showFilterMatchModes={false}
          onFilterClear={() => {
            const newFilters = { ...filters, ownerName: '' };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ minWidth: '8rem', textAlign: 'center' }}
        />
        <Column
          header={t('projectProposal.viewList.researchField')}
          headerStyle={{ ...filterActiveStyle('researchFieldId'), justifyContent: 'center' }}
          body={formatResearchFields}
          filter
          filterField="_researchField"
          filterElement={researchFieldFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMatchModes={false}
          showFilterMenu
          onFilterClear={() => {
            const newFilters = { ...filters, researchFieldId: [] };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ minWidth: '10rem', textAlign: 'center' }}
        />
        <Column
          hidden
          field="implementationMonths"
          header={t('projectProposal.viewList.implementationTime')}
          dataType="numeric"
          style={{ minWidth: '6rem', textAlign: 'center' }}
        />
        <Column
          field="program.name"
          header={t('projectProposal.viewList.program')}
          headerStyle={filterActiveStyle('programId')}
          dataType="date"
          filter
          filterField="_program"
          filterElement={programFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMatchModes={false}
          showFilterMenu
          onFilterClear={() => {
            const newFilters = { ...filters, programId: [] };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ minWidth: '8rem' }}
        />
        <Column
          header={t('projectProposal.viewList.council')}
          headerStyle={{
            ...filterActiveStyle('councilTypeId'),
            textAlign: 'center',
          }}
          body={formatCouncil}
          style={{ minWidth: '10rem', maxWidth: '16rem' }}
        />
        <Column
          field="status.name"
          header={t('projectProposal.viewList.status')}
          headerStyle={filterActiveStyle('statusName')}
          body={formatStatus}
          style={{ minWidth: '8rem', textAlign: 'center' }}
        />
      </DataTable>

      <Pagination
        pagination={pagination}
        setPagination={setPagination}
        paginationData={data?.pagination}
        showMode={showMode}
        setShowMode={setShowMode}
        pageName="projectProposals"
      />

      <Menu model={approvalItems} popup ref={approvalMenu} />

      <Confirm ref={dialogConfirmRef} refetch={refetch} />
      <RequestUpdateForm ref={dialogRequestUpdateRef} />

      <div className="flex flex-wrap justify-content-end mt-4">
        <Button
          label={t('formLayout.action.cancel')}
          severity="danger"
          onClick={() => setVisible(false)}
          className="mr-1 w-8rem"
        />
        <Button
          label={
            projectProposals.length === 0
              ? t('formLayout.action.add')
              : t('formLayout.action.update')
          }
          severity="info"
          type="submit"
          className="w-8rem"
          onClick={() => {
            // onSelectedChange((selected.filter((item) => selectedItems.find((item1) => item1.reference === item.reference))));
            onSelectedChange(selectedItems);
            setVisible(false);
          }}
          disabled={selectedItems.length === 0 || selectedItems === projectProposals}
        />
      </div>
    </div>
  );
  // #endregion Render
}

ViewListProjectProposal.propTypes = {
  onSelectedChange: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  projectProposals: PropTypes.array,
  setVisible: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  curMembers: PropTypes.array,
  // eslint-disable-next-line react/forbid-prop-types
  councilTypeSelected: PropTypes.object,
};
ViewListProjectProposal.defaultProps = {
  onSelectedChange: () => true,
  projectProposals: [],
  setVisible: () => true,
  curMembers: [],
  councilTypeSelected: {},
};
export default ViewListProjectProposal;
